
const libFableServiceProviderBase = require('fable-serviceproviderbase');

const libIntegrationAdapter = require('headlight-integration-adapter');
const libGUIDBatchResolver = libIntegrationAdapter.GUIDBatchResolver;
const libDeletedRecordReviver = require('./Headlight-Deleted-Record-Reviver.js');
const libComprehensionSource = require('./Comprehension-Source.js');

const SPECIAL_TABLES_KEYS = new Set(['OnSave', 'OnApprovalAction', 'OnEnteringState', 'ExecutionLog']);

class HeadlightComprehensionProcessor extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The Fable instance
	 * @param {Record<string, any>} [pOptions] - (optional) Options for the adapter
	 * @param {string} [pServiceHash] - (optional) Hash to use for the service
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & { HeadlightRestClient: import('headlight-rest-client') }} */
		this.fable;

		this.serviceType = 'ComprehensionProcessor';

		/** @type {import('headlight-rest-client')} */
		this._Client = null;

		/** @type {import('headlight-integration-adapter').GUIDBatchResolver} */
		this._GUIDBatchResolver = null;
	}

	/**
	 * Order the entity types so a referenced entity is always integrated before the entity that
	 * references it.
	 *
	 * Reads shapes only — never records — so this costs the same on a 200-row comprehension and a
	 * 200,000-row one. The two ordering forms are deliberately tested differently: a `GUID<Entity>`
	 * edge needs a populated value somewhere, while a `…_Via_GUID<Entity>` edge is established by the
	 * field name alone. `_GUID<Entity>` addresses a record that already exists on the server, so it
	 * establishes no edge at all.
	 *
	 * @param {import('./Comprehension-Source.js').ComprehensionSource|Object} pSource - A source, or
	 *        a raw comprehension, which is wrapped.
	 * @return {Array<string>} The entity types in dependency order.
	 */
	getAdapterSequence(pSource)
	{
		const tmpSource = libComprehensionSource.asComprehensionSource(pSource);
		/** @type {Record<string, Array<string>>} */
		const tmpDependencyMap = { };
		const tmpTables = tmpSource.EntityTypes.filter((pTable) => !SPECIAL_TABLES_KEYS.has(pTable));
		for (const tmpTable of tmpTables)
		{
			const tmpShape = tmpSource.EntityShapes[tmpTable] || libComprehensionSource.emptyShape();
			for (const tmpTableKey of tmpTables)
			{
				if (tmpTable === tmpTableKey)
				{
					continue;
				}
				// A direct reference counts only where some record populated it; an empty column is
				// not a dependency.
				let tmpHasDependency = (tmpShape.PopulatedFields.indexOf(`GUID${tmpTableKey}`) > -1);

				// `_Dest_IDEntity_*_Via_GUID{Table}` is established by the field name.
				if (!tmpHasDependency)
				{
					const tmpViaKey = `_Via_GUID${tmpTableKey}`;
					for (const tmpFieldKey of tmpShape.Fields)
					{
						if (tmpFieldKey.endsWith(tmpViaKey))
						{
							tmpHasDependency = true;
							break;
						}
					}
				}

				if (tmpHasDependency)
				{
					if (!tmpDependencyMap[tmpTable])
					{
						tmpDependencyMap[tmpTable] = [];
					}
					if (!tmpDependencyMap[tmpTable].includes(tmpTableKey))
					{
						this.fable.log.info(`Data dependency detected: [${tmpTableKey}] must come before [${tmpTable}]`);
						tmpDependencyMap[tmpTable].push(tmpTableKey);
					}
				}
			}
		}
		return this.topologicalSort(tmpTables, tmpDependencyMap);
	}

	/**
	 * Sort the adapters topologically based on their dependencies.
	 *
	 * @param {Array<string>} pNodes - The nodes to sort
	 * @param {Record<string, Array<string>>} pDependencyMap - The dependency map
	 *
	 * @return {Array<string>} - The sorted keys
	 */
	topologicalSort(pNodes, pDependencyMap)
	{
		/* Algorithm source: https://en.wikipedia.org/wiki/Topological_sorting#Kahn's_algorithm
		L ← Empty list that will contain the sorted elements
		S ← Set of all nodes with no incoming edges
		while S is non-empty do
			remove a node n from S
			add n to tail of L
			for each node m with an edge e from n to m do
				remove edge e from the graph
				if m has no other incoming edges then
					insert m into S
		if graph has edges then
			return error (graph has at least one cycle)
		else
			return L (a topologically sorted order)
		 */
		const tmpL = [];
		const tmpS = pNodes.filter((pNode) => !Object.keys(pDependencyMap).includes(pNode));
		const tmpDependencyMap = JSON.parse(JSON.stringify(pDependencyMap));

		while (tmpS.length > 0)
		{
			const tmpN = tmpS.pop();
			tmpL.push(tmpN);
			for (const tmpM in tmpDependencyMap)
			{
				if (tmpDependencyMap[tmpM].includes(tmpN))
				{
					tmpDependencyMap[tmpM] = tmpDependencyMap[tmpM].filter((pNode) => pNode !== tmpN);
					if (tmpDependencyMap[tmpM].length === 0)
					{
						tmpS.push(tmpM);
						delete tmpDependencyMap[tmpM];
					}
				}
			}
		}
		if (Object.keys(tmpDependencyMap).length > 0)
		{
			throw new Error(`Graph has at least one cycle: ${JSON.stringify(tmpDependencyMap)}`);
		}
		return tmpL;
	}

	/**
	 * @param {Record<string, Array<Record<string, any>> | Record<string, Record<string, any>>>} pComprehension - The comprehension to integrate
	 * @param {Record<string, Array<Record<string, any>> | Record<string, Record<string, any>>>} pPreviousComprehension - The previous comprehension to diff against. Any GUIDs not present in the new comprehension will be deleted.
	 * @param {(pError?: Error, pUpdatedComprehension?: Record<string, Array<Record<string, any>> | Record<string, Record<string, any>>>) => void} fCallback - A callback invoked when the integration is complete - if records are deleted, the updated comprehension after deletions will be passed as the second parameter
	 * @param {(pRecord: Record<string, any>, pTable: string) => boolean} [fCanRecordBeDeleted] - (optional) A function that determines if a record can be deleted. If not provided, all records not present in the new comprehension will be deleted.
	 */
	integrateComprehensionWithDiffDelete(pComprehension, pPreviousComprehension, fCallback, fCanRecordBeDeleted)
	{
		let tmpUpdatedComprehension = JSON.parse(JSON.stringify(pPreviousComprehension || { }));
		this.integrateComprehension(pComprehension, (pError) =>
		{
			// Surgically merge the new entity records (regular tables) into the copy of the previous
			// comprehension. Intent-typed special keys (OnSave/OnApprovalAction/OnEnteringState) live only on
			// Document.EntityData (never in the FormData entity-data copy); the copy above preserves them
			// untouched, and we skip them here so they are never merged, diffed, or deleted.
			for (const tmpTableKey in pComprehension)
			{
				if (SPECIAL_TABLES_KEYS.has(tmpTableKey))
				{
					continue;
				}
				const tmpRecordMap = pComprehension[tmpTableKey];
				for (const tmpRecordGUID in tmpRecordMap)
				{
					tmpUpdatedComprehension[tmpTableKey] = tmpUpdatedComprehension[tmpTableKey] || {};
					tmpUpdatedComprehension[tmpTableKey][tmpRecordGUID] = tmpRecordMap[tmpRecordGUID];
				}
			}
			if (!pPreviousComprehension || Object.keys(pPreviousComprehension).length === 0)
			{
				// No baseline to diff against (e.g. first save): nothing to delete; persist the merged comprehension.
				return fCallback(pError, tmpUpdatedComprehension);
			}
			const tmpRecordsToDelete = [];
			for (const tmpTableKey in pPreviousComprehension)
			{
				if (SPECIAL_TABLES_KEYS.has(tmpTableKey))
				{
					continue;
				}
				const tmpPreviousRecordMap = pPreviousComprehension[tmpTableKey];
				for (const tmpRecordGUID in tmpPreviousRecordMap)
				{
					if (pComprehension?.[tmpTableKey]?.[tmpRecordGUID])
					{
						continue;
					}
					if (typeof fCanRecordBeDeleted === 'function' && !fCanRecordBeDeleted(tmpPreviousRecordMap[tmpRecordGUID], tmpTableKey))
					{
						continue;
					}
					tmpRecordsToDelete.push({ Table: tmpTableKey, GUID: tmpRecordGUID });
					if (tmpUpdatedComprehension?.[tmpTableKey]?.[tmpRecordGUID])
					{
						delete tmpUpdatedComprehension[tmpTableKey][tmpRecordGUID];
					}
				}
			}
			if (tmpRecordsToDelete.length < 1)
			{
				// Nothing to delete, but the merged comprehension (new entities + preserved special keys) must persist.
				return fCallback(pError, tmpUpdatedComprehension);
			}
			const tmpClient = this.client;
			const tmpAnticipate = this.fable.newAnticipate();
			tmpAnticipate.anticipate(
				(fNext) =>
				{
					tmpClient.getJSON('CheckSession', (pError, pSession) =>
					{
						if (!pError && pSession?.LoggedIn)
						{
							return fNext();
						}
						tmpClient.authenticate(
							(pError, pResponse) =>
							{
								if (pError)
								{
									this.log.error(`Error authenticating`, pError);
								}
								else
								{
									this.log.info(`Authenticated - Session [${pResponse.SessionID}]`);
								}
								return fNext(pError);
							});
					});
				});

			tmpAnticipate.anticipate(
				(fNext) =>
				{
					this.fable.log.info(`Going to delete ${tmpRecordsToDelete.length} records by GUID...`);
					/** @type {Map<string, Set<string>>} */
					const tmpDeleteTargets = new Map();
					for (const tmpRecord of tmpRecordsToDelete)
					{
						if (!tmpDeleteTargets.has(tmpRecord.Table))
						{
							tmpDeleteTargets.set(tmpRecord.Table, new Set());
						}
						tmpDeleteTargets.get(tmpRecord.Table).add(tmpRecord.GUID);
					}
					this.guidBatchResolver.resolveReferences(tmpDeleteTargets)
						.then(() => fNext())
						.catch((pError) =>
						{
							this.fable.log.warn(`Could not resolve records to delete in batches: ${pError.message || pError}`);
							return fNext();
						});
				});
			const fResolveRecordID = (pTable, pGUID) =>
			{
				const tmpGUIDMap = this.guidBatchResolver.guidMap;
				return tmpGUIDMap.getHeadlightIDFromExternalGUID(pTable, pGUID) || tmpGUIDMap.getIDFromGUID(pTable, pGUID);
			};
			for (const tmpRecord of tmpRecordsToDelete)
			{
				tmpAnticipate.anticipate(
					(fNext) =>
					{
						const tmpIDRecord = fResolveRecordID(tmpRecord.Table, tmpRecord.GUID);
						if (!tmpIDRecord)
						{
							this.fable.log.warn(`Record to delete not found: ${tmpRecord.Table} - ${tmpRecord.GUID}`);
							return fNext();
						}
						tmpClient.deleteEntity(tmpRecord.Table, tmpIDRecord, (pError) =>
							{
								if (pError)
								{
									this.fable.log.error(`Error deleting record: ${tmpRecord.Table} - ${tmpRecord.GUID}`, pError);
								}
								return fNext();
							});
					});
			}

			tmpAnticipate.wait(
				(pError) =>
				{
					if (pError)
					{
						this.fable.log.error(`Error importing comprehension file.`, pError);
						return fCallback(pError, tmpUpdatedComprehension);
					}
					this.fable.log.info(`Finished importing comprehension file.`);
					return fCallback(null, tmpUpdatedComprehension);
				});
		});
	}

	/**
	 * @return {import('headlight-rest-client')}
	 */
	get client()
	{
		if (this.options.UseSiloedClient)
		{
			if (!this._Client)
			{
				this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
				this._Client = this.fable.serviceManager.instantiateServiceProviderWithoutRegistration('HeadlightRestClient', this.options);
			}
			return this._Client;
		}

		let tmpClient = this.fable.services.HeadlightRestClient;
		if (!tmpClient)
		{
			this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
			if (!this.options.hasOwnProperty('HeadlightAPIURL'))
			{
				this.options.HeadlightAPIURL = 'https://api.qa.headlight.com/1.0/';
			}
			tmpClient = this.fable.serviceManager.instantiateServiceProviderWithoutRegistration('HeadlightRestClient', this.options);
		}
		return tmpClient;
	}

	set client(pClient)
	{
		this._Client = pClient;
	}

	/**
	 * Build the option set for one entity's integration adapter. The caller's
	 * upsert tuning — RecordThresholdForBulkUpsert, BulkUpsertBatchSize,
	 * RecordPushRetryThreshold — is forwarded when supplied as a positive
	 * integer on the processor's options; the adapter's own defaults apply
	 * otherwise.
	 *
	 * @param {string} pEntity - The entity the adapter will integrate.
	 * @return {Record<string, any>} Options for a headlight-integration-adapter instance.
	 */
	buildAdapterOptions(pEntity)
	{
		/** @type {Record<string, any>} */
		const tmpOptions =
			{
				Entity: pEntity,
				EntityGUIDMarshalPrefix: '',
				AdapterSetGUIDMarshalPrefix: '',
				SimpleMarshal: true,
				ForceMarshal: true,
			};
		for (const tmpTuningKey of [ 'RecordThresholdForBulkUpsert', 'BulkUpsertBatchSize', 'RecordPushRetryThreshold' ])
		{
			const tmpValue = parseInt(this.options[tmpTuningKey], 10);
			if (Number.isFinite(tmpValue) && tmpValue > 0)
			{
				tmpOptions[tmpTuningKey] = tmpValue;
			}
		}
		return tmpOptions;
	}

	/**
	 * Revive any soft-deleted records the comprehension is about to upsert.
	 *
	 * Enabled by the `ReviveDeletedRecords` option. Off by default because the
	 * scan costs a paged read per entity, and most loads never touch a deleted
	 * GUID; a pipeline that deletes and later restores records wants it on.
	 *
	 * @param {Record<string, any>} pComprehension
	 * @param {(pError?: Error, pSummary?: Object) => void} fCallback
	 */
	reviveDeletedRecords(pSource, fCallback)
	{
		const tmpSource = libComprehensionSource.asComprehensionSource(pSource);
		let tmpEntitySequence;
		try
		{
			tmpEntitySequence = this.getAdapterSequence(tmpSource).filter((pEntity) => !SPECIAL_TABLES_KEYS.has(pEntity));
		}
		catch (pError)
		{
			return fCallback(pError);
		}

		this.fable.serviceManager.addServiceType('DeletedRecordReviver', libDeletedRecordReviver);
		const tmpReviver = this.fable.serviceManager.instantiateServiceProviderWithoutRegistration('DeletedRecordReviver',
			{ ScanLimit: this.options.RevivalScanLimit });
		tmpReviver.client = this.client;

		// Per entity, per batch: `reviveEntity` answers a tally, so batches sum rather than needing
		// the whole comprehension resident.
		const tmpResults = [];
		let tmpTotalRevived = 0;
		let tmpTotalFailed = 0;
		const fReviveEntity = (pIndex, fDone) =>
		{
			if (pIndex >= tmpEntitySequence.length)
			{
				return fDone();
			}
			const tmpEntity = tmpEntitySequence[pIndex];
			return tmpSource.streamRecords(tmpEntity,
				(pRecords, fNext) =>
				{
					tmpReviver.reviveEntity(tmpEntity, pRecords)
						.then((pResult) =>
						{
							tmpResults.push(pResult);
							tmpTotalRevived += pResult.Revived;
							tmpTotalFailed += pResult.Failed;
							return fNext();
						})
						.catch((pError) => fNext(pError));
				},
				(pError) => (pError ? fDone(pError) : fReviveEntity(pIndex + 1, fDone)));
		};

		return fReviveEntity(0, (pError) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				return fCallback(null, { Entities: tmpResults, TotalRevived: tmpTotalRevived, TotalFailed: tmpTotalFailed });
			});
	}

	/**
	 * The batch resolver shared by every entity in one comprehension load.
	 *
	 * @return {import('headlight-integration-adapter').GUIDBatchResolver}
	 */
	get guidBatchResolver()
	{
		if (!this._GUIDBatchResolver)
		{
			this.fable.serviceManager.addServiceType('IntegrationGUIDBatchResolver', libGUIDBatchResolver);
			this._GUIDBatchResolver = this.fable.serviceManager.instantiateServiceProviderWithoutRegistration('IntegrationGUIDBatchResolver',
				this.options.GUIDBatchResolutionOptions || {});
		}
		this._GUIDBatchResolver.client = this.client;
		return this._GUIDBatchResolver;
	}

	/**
	 * Ask the server, once, whether it serves the body-driven read route, so the
	 * batch resolver can send large IN lists in a request body instead of
	 * chunking them across URI-bounded reads.  The capability rides along on
	 * every entity's schema, so any entity in the comprehension answers it.
	 *
	 * @param {Array<string>} pEntitySequence - The entities being loaded.
	 * @param {() => void} fCallback - Invoked once the capability is known (or known to be unknowable).
	 */
	loadBatchReadCapability(pEntitySequence, fCallback)
	{
		const tmpResolver = this.guidBatchResolver;
		if (typeof(tmpResolver.QueryEndpointSupported) === 'boolean' || pEntitySequence.length < 1)
		{
			return fCallback();
		}
		this.client.getJSON(`${pEntitySequence[0]}/Schema`,
			(pError, pSchema) =>
			{
				if (pError)
				{
					this.fable.log.info(`Could not read the [${pEntitySequence[0]}] schema to detect batch read capabilities; using URI reads.`);
					tmpResolver.QueryEndpointSupported = false;
					return fCallback();
				}
				tmpResolver.applySchemaCapabilities(pSchema);
				return fCallback();
			});
	}

	/**
	 * Resolve every GUID the comprehension references, across all of its
	 * entities, in batched reads before any adapter runs.
	 *
	 * The per-entity adapters do the same thing for their own slice, but doing
	 * it once for the whole comprehension collapses what would otherwise be one
	 * batch round-trip per entity into a handful -- which is what the browser
	 * save path (many entities, few records each) actually spends its time on.
	 * Only hits are cached: a reference whose target this run is about to create
	 * has to stay resolvable later.
	 *
	 * @param {Record<string, any>} pComprehension - The comprehension to scan.
	 * @param {Array<string>} pEntitySequence - The entities being loaded.
	 * @param {(pError?: Error, pSummary?: Object) => void} fCallback - Invoked when the prefetch is done.
	 */
	prefetchComprehensionGUIDs(pSource, pEntitySequence, fCallback)
	{
		const tmpSource = libComprehensionSource.asComprehensionSource(pSource);
		/** @type {Map<string, Set<string>>} */
		let tmpReferences = new Map();

		// The collector merges into an existing map, so batches fold in one at a time and the map
		// stays bounded by distinct referenced GUIDs rather than by record count.
		const tmpEntities = pEntitySequence.filter((pEntity) => !SPECIAL_TABLES_KEYS.has(pEntity));
		const fCollectEntity = (pIndex, fDone) =>
		{
			if (pIndex >= tmpEntities.length)
			{
				return fDone();
			}
			const tmpEntity = tmpEntities[pIndex];
			return tmpSource.streamRecords(tmpEntity,
				(pRecords, fNext) =>
				{
					tmpReferences = libGUIDBatchResolver.collectMappedGUIDReferences(pRecords, `GUID${tmpEntity}`, tmpReferences);
					return fNext();
				},
				(pError) => (pError ? fDone(pError) : fCollectEntity(pIndex + 1, fDone)));
		};

		return fCollectEntity(0, (pStreamError) =>
		{
		if (pStreamError)
		{
			this.fable.log.warn(`Could not read records for GUID pre-resolution; entity adapters will resolve references themselves: ${pStreamError.message || pStreamError}`);
			return fCallback();
		}
		if (tmpReferences.size < 1)
		{
			return fCallback();
		}

		this.loadBatchReadCapability(pEntitySequence,
			() =>
			{
				this.guidBatchResolver.resolveReferences(tmpReferences)
					.then((pSummary) =>
					{
						const tmpUnverifiedNote = (pSummary.Unverified > 0) ? `, ${pSummary.Unverified} left to single lookups` : '';
						this.fable.log.info(`Pre-resolved ${pSummary.Resolved} of ${pSummary.Requested} referenced GUID(s) across ${tmpReferences.size} entities in ${pSummary.Requests} batch read(s)${tmpUnverifiedNote}.`);
						return fCallback(null, pSummary);
					})
					.catch((pError) =>
					{
						this.fable.log.warn(`Batch GUID pre-resolution did not complete; entity adapters will resolve references themselves: ${pError.message || pError}`);
						return fCallback();
					});
			});
		});
	}

	/**
	 * @param {Record<string, Array<Record<string, any>> | Record<string, Record<string, any>>>} pComprehension - The comprehension to integrate
	 * @param {(pError?: Error) => void} fCallback - A callback invoked when the integration is complete
	 */
	integrateComprehension(pComprehension, fCallback)
	{
		const tmpSource = libComprehensionSource.asComprehensionSource(pComprehension);
		const tmpClient = this.client;
		const tmpAnticipate = this.fable.newAnticipate();

		// Track stats for the summary
		let tmpStats = { entities: {}, startTime: Date.now(), warnings: 0, errors: 0, revived: 0 };

		// Intercept log warnings/errors to count them
		let tmpOriginalWarn = this.fable.log.warn.bind(this.fable.log);
		let tmpOriginalError = this.fable.log.error.bind(this.fable.log);
		this.fable.log.warn = (...pArgs) => { tmpStats.warnings++; return tmpOriginalWarn(...pArgs); };
		this.fable.log.error = (...pArgs) => { tmpStats.errors++; return tmpOriginalError(...pArgs); };

		this.fable.log.info(`Initializing and configuring data integration adapters...`);
		this.fable.serviceManager.addServiceType('IntegrationAdapter', libIntegrationAdapter);

		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				tmpClient.getJSON('CheckSession', (pError, pSession) =>
				{
					if (!pError && pSession?.LoggedIn)
					{
						return fCallback();
					}
					tmpClient.authenticate(
						(pError, pResponse) =>
						{
							if (pError)
							{
								this.log.error(`Error authenticating`, pError);
							}
							else
							{
								this.log.info(`Authenticated - Session [${pResponse.SessionID}]`);
							}
							return fCallback(pError);
						});
				});
			});

		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				if (!this.options.ReviveDeletedRecords)
				{
					return fCallback();
				}
				// Upsert cannot see a soft-deleted row, so a GUID belonging to one
				// would create a silent duplicate instead of reviving the original.
				// Revive them first, before any adapter runs.
				this.reviveDeletedRecords(tmpSource,
					(pError, pSummary) =>
					{
						if (pError)
						{
							this.fable.log.error(`Error reviving soft-deleted records; continuing with the push.`, pError);
						}
						else if (pSummary && pSummary.TotalRevived > 0)
						{
							tmpStats.revived = pSummary.TotalRevived;
							this.fable.log.info(`Revived ${pSummary.TotalRevived} soft-deleted record(s) before upsert.`);
						}
						return fCallback();
					});
			});

		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				if (this.options.BatchGUIDResolution === false)
				{
					return fCallback();
				}
				let tmpEntitySequence;
				try
				{
					tmpEntitySequence = this.getAdapterSequence(tmpSource);
				}
				catch (pError)
				{
					// A dependency cycle is reported by the wiring stage below.
					return fCallback();
				}
				return this.prefetchComprehensionGUIDs(tmpSource, tmpEntitySequence, () => fCallback());
			});

		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				this.fable.log.info(`Wiring up Integration Adapters...`);

				const tmpIntegrationAdapterSet = this.getAdapterSequence(tmpSource);
				this.fable.log.info(`Integration Adapter sequence: ${tmpIntegrationAdapterSet.join(' -> ')}`);

				try
				{
					for (let i = 0; i < tmpIntegrationAdapterSet.length; i++)
					{
						// Initialize the adapter
						const tmpAdapterKey = tmpIntegrationAdapterSet[i];
						const tmpRecordCount = tmpSource.recordCount(tmpAdapterKey);
						tmpStats.entities[tmpAdapterKey] = { input: tmpRecordCount };

						if (tmpRecordCount === 0)
						{
							//NOTE: if you invoke the adapter with zero records, it logs an error; just skip, nothing to do
							continue;
						}

						/** @type {libIntegrationAdapter} */
						const tmpAdapter = this.fable.instantiateServiceProviderWithoutRegistration('IntegrationAdapter', this.buildAdapterOptions(tmpAdapterKey));
						tmpAdapter.client = tmpClient;
						this.runAdapter(tmpAnticipate, tmpAdapter, tmpSource, tmpAdapterKey);
					}
				}
				catch (pError)
				{
					this.fable.log.error(`Error wiring up integration adapters: ${pError}`, pError);
					return fCallback(pError);
				}

				return fCallback();
			});

		tmpAnticipate.wait(
			(pError) =>
			{
				// Restore original log methods
				this.fable.log.warn = tmpOriginalWarn;
				this.fable.log.error = tmpOriginalError;

				// Print summary
				let tmpElapsed = ((Date.now() - tmpStats.startTime) / 1000).toFixed(1);
				this.fable.log.info(`\n========== Integration Summary ==========`);
				this.fable.log.info(`Duration: ${tmpElapsed}s`);
				this.fable.log.info(`Entity records pushed:`);
				for (let tmpEntity in tmpStats.entities)
				{
					this.fable.log.info(`  ${tmpEntity}: ${tmpStats.entities[tmpEntity].input}`);
				}
				if (tmpStats.revived > 0)
				{
					this.fable.log.info(`Soft-deleted records revived before upsert: ${tmpStats.revived}`);
				}
				if (tmpStats.warnings > 0)
				{
					this.fable.log.warn(`Warnings: ${tmpStats.warnings} (review log output above for details)`);
				}
				if (tmpStats.errors > 0)
				{
					this.fable.log.error(`Errors: ${tmpStats.errors} (review log output above for details)`);
				}
				if (tmpStats.warnings === 0 && tmpStats.errors === 0)
				{
					this.fable.log.info(`No warnings or errors.`);
				}
				this.fable.log.info(`==========================================\n`);

				if (pError)
				{
					this.fable.log.error(`Error importing comprehension file.`, pError);
					return fCallback(pError);
				}
				this.fable.log.info(`Finished importing comprehension file.`);
				return fCallback(pError);
			});
	}

	/**
	 * Feed one entity's records into its adapter, a batch at a time.
	 *
	 * The adapter still accumulates what it is given before `integrateRecords` runs, so this bounds
	 * what the PROCESSOR holds, not yet what the adapter does — see the note in the class header.
	 *
	 * @param {any} pAnticipate - The anticipate instance - FIXME: not exporting this interface - fake types anyway
	 * @param {import('headlight-integration-adapter')} pAdapter - The adapter to feed
	 * @param {import('./Comprehension-Source.js').ComprehensionSource} pSource - The record source
	 * @param {string} pEntity - The entity type to stream
	 * @param {(pRecord: Record<string, any>) => Record<string, any>} [fMarshalRecord] - (optional) A function to marshal each record before integration
	 */
	runAdapter(pAnticipate, pAdapter, pSource, pEntity, fMarshalRecord)
	{
		let tmpAdapter = pAdapter;

		if (!tmpAdapter)
		{
			throw new Error(`Adapter not found.`);
		}
		let tmpPrefix = this.options.prefix || this.fable.settings.prefix;
		if (tmpPrefix)
		{
			tmpAdapter.AdapterSetGUIDMarshalPrefix = tmpPrefix;
		}

		let tmpMarshalRecordFunction = fMarshalRecord;
		pAnticipate.anticipate(
			(fDone) =>
			{
				pSource.streamRecords(pEntity,
					(pRecords, fNext) =>
					{
						for (const tmpRecord of pRecords)
						{
							tmpAdapter.addSourceRecord(tmpRecord);
						}
						return fNext();
					},
					fDone);
			});
		pAnticipate.anticipate(
			(fDone) =>
			{
				tmpAdapter.integrateRecords(fDone, tmpMarshalRecordFunction);
			});
	}

	getCapitalLettersAsString(inputString)
	{
		let tmpRegex = /[A-Z]/g;
		let tmpMatch = inputString.match(tmpRegex);
		let tmpString = tmpMatch ? tmpMatch.join('') : 'UNK';
		return tmpString;
	}
}

module.exports = HeadlightComprehensionProcessor;
/** @type {Record<string, any>} */
module.exports.default_configuration = { };
